import { supabase } from '@/lib/supabase';

export type QRCampaign = {
  id: string;
  name: string;
  company_id: string;
  destination_url: string;
  tracking_domain?: string | null;
  marketing_source?: string | null;
  marketing_campaign_id?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  cost_cents?: number;
  created_at?: string;
  updated_at?: string;
};

export type QRCampaignDetail = {
  campaign: QRCampaign;
  counts: {
    codes: number;
    scans: number;
    unique_scans: number;
  };
};

export type QRCodeRow = {
  id: string;
  short_code: string;
  customer_id: string | null;
  created_at: string;
  tracking_url: string;
  qr_image_url: string;
};

export type QRScanRow = {
  id: string;
  qr_code_id: string;
  device_id: string | null;
  is_unique: boolean;
  scanned_at: string;
  city: string | null;
  region: string | null;
  country: string | null;
  postal: string | null;
  user_agent: string | null;
  referer: string | null;
};

function getApiBase(): string {
  return (
    import.meta.env.VITE_MARKETING_API_URL ||
    import.meta.env.VITE_API_URL ||
    'http://localhost:3004'
  );
}

async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const apiBase = getApiBase();

  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  };

  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${apiBase}${path}`, {
    ...init,
    headers,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Request failed: ${res.status}`);
  }

  return (await res.json()) as T;
}

export async function listQRCampaigns(companyId?: string): Promise<QRCampaign[]> {
  const q = companyId ? `?company_id=${encodeURIComponent(companyId)}` : '';
  const res = await apiRequest<{ success: boolean; data: QRCampaign[] }>(`/api/qr/campaigns${q}`, {
    method: 'GET',
  });
  return res.data || [];
}

export async function createQRCampaign(input: {
  name: string;
  company_id: string;
  destination_url: string;
  marketing_source?: string;
  tracking_domain?: string;
  marketing_campaign_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  cost_cents?: number;
}): Promise<QRCampaign> {
  const res = await apiRequest<{ success: boolean; data: QRCampaign }>(`/api/qr/campaigns`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function getQRCampaign(id: string): Promise<QRCampaignDetail> {
  const res = await apiRequest<{ success: boolean; data: QRCampaignDetail }>(`/api/qr/campaigns/${id}`, {
    method: 'GET',
  });
  return res.data;
}

export async function generateQRCodes(campaignId: string, input: {
  quantity?: number;
  recipients?: Array<{ customer_id?: string; destination_url?: string; metadata?: any }>;
}): Promise<QRCodeRow[]> {
  const res = await apiRequest<{ success: boolean; data: QRCodeRow[] }>(`/api/qr/campaigns/${campaignId}/codes`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return res.data || [];
}

export async function listQRCodes(campaignId: string, limit: number = 200): Promise<QRCodeRow[]> {
  const res = await apiRequest<{ success: boolean; data: QRCodeRow[] }>(`/api/qr/campaigns/${campaignId}/codes?limit=${limit}`, {
    method: 'GET',
  });
  return res.data || [];
}

export async function listQRScans(campaignId: string, limit: number = 200): Promise<QRScanRow[]> {
  const res = await apiRequest<{ success: boolean; data: QRScanRow[] }>(`/api/qr/campaigns/${campaignId}/scans?limit=${limit}`, {
    method: 'GET',
  });
  return res.data || [];
}
